import { CircularProgress } from '@mui/material';
import { filter, isEmpty } from 'lodash';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { internalWalletsApi, usersApi } from 'api';
import {
  CopyText,
  CrudPage,
  CrudTableActionType,
  DataGridColumnDefinition,
  dataGridColumns,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { WalletInformationIcon, WalletResources } from 'features/wallets';
import { useMutation, usePartialQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, InternalWallet, WalletResourcesData } from 'types';
import { formatUtils } from 'utils';

type UserWalletsFilters = {
  userId: string;
  address: string;
};

export const InternalUserWallets: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.internal_wallets',
  });

  const [resources, setResources] = useState<{
    [id: string]: WalletResourcesData;
  }>({});
  const [resourcesLoading, setResourcesLoading] = useState<string[]>([]);

  const queryResult = usePartialQuery(
    QueryKey.InternalUsersWallets,
    internalWalletsApi.getAllOfUsersPaginated,
  );

  const { mutate: getResources } = useMutation(
    internalWalletsApi.getResources,
    {
      onMutate: (itemId: string) => {
        setResourcesLoading((data) => [...data, itemId]);
      },
      onSuccess: (data) => {
        setResources((resources) => ({
          ...resources,
          [data.id]: data,
        }));
      },
      onSettled: (data, error, itemId: string) => {
        setResourcesLoading((data) => filter(data, (id) => id !== itemId));
      },
      notifierType: 'none',
    },
  );

  const queryResultInternalWalletHolders = useQuery(
    QueryKey.InternalWalletHolders,
    usersApi.getInternalWalletHolders,
  );

  const filtersDefinitions: FilterDefinition<UserWalletsFilters>[] = useMemo(
    () => [
      {
        label: t('fields.user'),
        name: 'userId',
        type: FilterDefinitionType.User,
        users: queryResultInternalWalletHolders.data,
      },
      {
        label: t('fields.address'),
        name: 'address',
        type: FilterDefinitionType.Text,
      },
    ],
    [queryResultInternalWalletHolders.data, t],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<InternalWallet>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.user'),
        valueGetter: (item) => (
          <Fragment>
            <div>{item.user?.name}</div>
            {item.shop?.name && (
              <div>{`(${formatUtils.formatName(item.shop)})`}</div>
            )}
          </Fragment>
        ),
      },
      {
        header: t('fields.address'),
        valueGetter: (item) => (
          <Fragment>
            <div className="tw-uppercase">{item.network}</div>
            <CopyText text={item.address} />
          </Fragment>
        ),
      },
      {
        header: t('fields.resources'),
        valueGetter: (item) => {
          if (resourcesLoading.includes(item.id)) {
            return <CircularProgress size={20} />;
          }

          return (
            <WalletResources
              resources={resources[item.id]}
              disabled={!isEmpty(resourcesLoading)}
              getResources={() => getResources(item.id)}
            />
          );
        },
      },
    ],
    [t, getResources, resources, resourcesLoading],
  );

  return (
    <CrudPage
      filters={{ filtersDefinitions }}
      table={{
        columns,
        queryResult,
        paginated: true,
        actions: [
          {
            type: CrudTableActionType.Custom,
            render: (item) => <WalletInformationIcon item={item} />,
          },
        ],
      }}
    />
  );
};
